import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendingUp, TrendingDown, Bitcoin, DollarSign, Search, RefreshCw, Gem, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { MarketResearchSection } from '../components/research/MarketResearchSection';

export default function LiveMarketsPage() {
  const [activeTab, setActiveTab] = useState('crypto');
  const [cryptoData, setCryptoData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [metalData, setMetalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchMarketData = useCallback(async () => {
    setLoading(true);
    try {
      const [cryptoRes, stockRes, metalRes] = await Promise.all([
        api.get('/markets/crypto'),
        api.get('/markets/stocks'),
        api.get('/markets/metals')
      ]);
      
      setCryptoData(cryptoRes.data.data || []);
      setStockData(stockRes.data.data || []);
      setMetalData(metalRes.data.data || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch market data:', error);
      toast.error('Failed to load market data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarketData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchMarketData, 60000);
    return () => clearInterval(interval);
  }, [fetchMarketData]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '$0.00';
    if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toFixed(6)}`;
  };

  const formatMarketCap = (cap) => {
    if (!cap) return 'N/A';
    if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`;
    if (cap >= 1e9) return `$${(cap / 1e9).toFixed(2)}B`;
    if (cap >= 1e6) return `$${(cap / 1e6).toFixed(2)}M`;
    return `$${cap.toLocaleString()}`;
  };

  const filteredCrypto = cryptoData.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStocks = stockData.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const MarketCard = ({ item, type }) => (
    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer border border-white/5">
      <div className="flex items-center gap-4">
        {type === 'crypto' && item.image && (
          <img src={item.image} alt={item.name} className="w-10 h-10 rounded-full" />
        )}
        {type === 'stock' && (
          <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#00E676]" />
          </div>
        )}
        {type === 'metal' && (
          <div className="w-10 h-10 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
            <Gem className="w-5 h-5 text-[#F7931A]" />
          </div>
        )}
        <div>
          <p className="font-medium text-white">{item.name}</p>
          <p className="text-sm text-gray-400">{item.symbol}</p>
        </div>
      </div>
      
      <div className="text-right">
        <p className="font-mono text-white">{formatPrice(item.price)}</p>
        <div className={`flex items-center justify-end gap-1 text-sm ${
          (item.change_24h || item.change_percent || 0) >= 0 ? 'text-[#00E676]' : 'text-red-400'
        }`}>
          {(item.change_24h || item.change_percent || 0) >= 0 ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{(item.change_24h || item.change_percent || 0).toFixed(2)}%</span>
        </div>
      </div>
      
      {type === 'crypto' && item.market_cap && (
        <div className="hidden md:block text-right min-w-[100px]">
          <p className="text-xs text-gray-500">Market Cap</p>
          <p className="text-sm text-gray-300">{formatMarketCap(item.market_cap)}</p>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="live-markets-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Live Markets
            </h1>
            <p className="text-gray-400 mt-1">
              Real-time prices for crypto, stocks, and metals
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchMarketData}
              disabled={loading}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {lastUpdate && (
              <span className="text-xs text-gray-500">
                Updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search markets..."
            className="input-dark pl-10"
            data-testid="market-search"
          />
        </div>

        {/* Market Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger 
              value="crypto" 
              className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black"
            >
              <Bitcoin className="w-4 h-4 mr-2" />
              Crypto ({cryptoData.length})
            </TabsTrigger>
            <TabsTrigger 
              value="stocks"
              className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Stocks ({stockData.length})
            </TabsTrigger>
            <TabsTrigger 
              value="metals"
              className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black"
            >
              <Gem className="w-4 h-4 mr-2" />
              Metals ({metalData.length})
            </TabsTrigger>
          </TabsList>

          {/* Crypto Tab */}
          <TabsContent value="crypto" className="mt-4">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bitcoin className="w-5 h-5 text-[#F7931A]" />
                  Top Cryptocurrencies
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#00E676] animate-spin" />
                  </div>
                ) : filteredCrypto.length > 0 ? (
                  <div className="space-y-2">
                    {filteredCrypto.map((coin, idx) => (
                      <MarketCard key={coin.symbol || idx} item={coin} type="crypto" />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8">No cryptocurrencies found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stocks Tab */}
          <TabsContent value="stocks" className="mt-4">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#00E676]" />
                  Top Stocks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#00E676] animate-spin" />
                  </div>
                ) : filteredStocks.length > 0 ? (
                  <div className="space-y-2">
                    {filteredStocks.map((stock, idx) => (
                      <MarketCard key={stock.symbol || idx} item={stock} type="stock" />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8">
                    Stock data is loading. Due to API rate limits, this may take a moment.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Metals Tab */}
          <TabsContent value="metals" className="mt-4">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Gem className="w-5 h-5 text-[#F7931A]" />
                  Precious Metals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#00E676] animate-spin" />
                  </div>
                ) : metalData.length > 0 ? (
                  <div className="space-y-2">
                    {metalData.map((metal, idx) => (
                      <MarketCard key={metal.symbol || idx} item={metal} type="metal" />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8">No metals data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Market Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">BTC Price</p>
                  <p className="text-xl font-bold text-white">
                    {cryptoData.find(c => c.symbol === 'BTC')?.price 
                      ? formatPrice(cryptoData.find(c => c.symbol === 'BTC').price)
                      : 'Loading...'}
                  </p>
                </div>
                <Bitcoin className="w-8 h-8 text-[#F7931A]" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">ETH Price</p>
                  <p className="text-xl font-bold text-white">
                    {cryptoData.find(c => c.symbol === 'ETH')?.price
                      ? formatPrice(cryptoData.find(c => c.symbol === 'ETH').price)
                      : 'Loading...'}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#627EEA]/20 flex items-center justify-center">
                  <span className="text-[#627EEA] font-bold">Ξ</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Gold Price</p>
                  <p className="text-xl font-bold text-white">
                    {metalData.find(m => m.symbol === 'GOLD')?.price
                      ? formatPrice(metalData.find(m => m.symbol === 'GOLD').price)
                      : 'Loading...'}
                  </p>
                </div>
                <Gem className="w-8 h-8 text-[#F7931A]" />
              </div>
            </CardContent>
          </Card>
        </div>

        <MarketResearchSection />
      </div>
    </DashboardLayout>
  );
}
